import { Link } from "react-router-dom";
import { Phone, HeartHandshake, MapPin, ShieldCheck } from "lucide-react";
import SEO from "@/components/SEO";
import { PHONE, PHONE_TEL, NAP_ADDRESS } from "@/components/PublicLayout";

const VALUES = [
  { Icon: HeartHandshake, title: "Genuinely Care", text: "We're more than a hire company — we care about keeping families comfortable in their own homes during disruptive times." },
  { Icon: MapPin, title: "Local & Reliable", text: "Based in Weybridge, we know Surrey inside out and pride ourselves on prompt, dependable delivery and support." },
  { Icon: ShieldCheck, title: "Trusted Service", text: "Five-star rated on Trustpilot and Facebook, with spotless pods, flexible dates and responsive support throughout your hire." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <SEO
        title="About Us | Mobile Kitchen Hire Surrey Specialists"
        description="Mobile Kitchen Company is a local Surrey business dedicated to keeping homeowners in their homes during renovations with mobile and temporary kitchen hire across Surrey."
      />
      <section className="bg-[#F8FAFC] border-b border-slate-200 py-14">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-4xl sm:text-5xl font-bold text-[#1A237E]">About Mobile Kitchen Company</h1>
          <p className="mt-5 text-lg text-slate-600 max-w-3xl leading-relaxed">
            A local Surrey business on a simple mission: keeping you in your home while your kitchen is out of action.
          </p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-12 items-start">
          <div className="space-y-5 text-base text-slate-700 leading-relaxed">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1A237E]">Our Mission</h2>
            <p>
              Mobile Kitchen Company (UK) Limited was founded after we recognised a real gap in the building
              and insurance industry. Time and again, homeowners across Surrey were being forced out of their
              properties — into hotels, rented accommodation or relatives' spare rooms — simply because their
              kitchen was out of action during renovation works, water damage repairs or building projects.
            </p>
            <p>
              Our mission is simple: to provide a convenient, reliable temporary kitchen that allows you to
              remain at home — comfortably and safely — no matter what work is being done. With our mobile
              kitchen hire in Surrey, there's no need to uproot the family, put pets into kennels or live off
              takeaways for weeks on end.
            </p>
            <p>
              Based at {NAP_ADDRESS}, we provide temporary kitchen hire across Surrey, from Weybridge and
              Walton-on-Thames to Guildford, Woking and beyond. Every pod is delivered, connected and
              explained by our own friendly team, and collected once your project is complete.
            </p>
            <p>
              Whether your kitchen is being refitted, your home has suffered water damage, or you're
              mid-way through a building project, our temporary kitchen hire Surrey service keeps family
              life running — cooking, washing and living as normal, right outside your back door.
            </p>
          </div>
          <div className="grid gap-6">
            {VALUES.map(({ Icon, title, text }) => (
              <div key={title} className="flex gap-5 border border-slate-200 rounded p-6 bg-[#F8FAFC]" data-testid={`value-card-${title.toLowerCase().replace(/[\s&]+/g, "-")}`}>
                <div className="shrink-0 h-12 w-12 rounded bg-[#1A237E] flex items-center justify-center">
                  <Icon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#1A237E]">{title}</h3>
                  <p className="mt-1 text-sm text-slate-600 leading-relaxed">{text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#1A237E] py-16" data-testid="about-cta">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white">Stay in Your Home — We'll Bring the Kitchen to You</h2>
          <p className="mt-4 text-lg text-slate-300 max-w-2xl mx-auto">
            Talk to our local team about mobile kitchen hire in Surrey today.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/contact" data-testid="about-quote-button" className="px-7 py-3.5 bg-white text-[#1A237E] font-semibold rounded hover:bg-slate-100 transition-colors">
              Get a Free Quote
            </Link>
            <a href={PHONE_TEL} data-testid="about-call-button" className="flex items-center gap-2 px-7 py-3.5 bg-white text-[#1A237E] font-semibold rounded hover:bg-slate-100 transition-colors">
              <Phone className="h-5 w-5" /> Call {PHONE}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
